import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import Register from './components/Register';
import MemoList from './components/MemoList';
import MemoEditor from './components/MemoEditor';
import CategoryManager from './components/CategoryManager';
import HandwritingPanel from './components/HandwritingPanel';
import HandwritingPreview from './components/HandwritingPreview';
import apiClient from './api';
import './App.css';
import './styles/github.css';
import MemoLogo from './components/MemoLogo';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import GamePage from './pages/GamePage';
import ConquestLobby from './components/ConquestLobby';
import GameCenter from './pages/GameCenter';
import MinesweeperLobby from './components/MinesweeperLobby';
import FlappyBirdLobby from './components/FlappyBirdLobby';
function App() {
  const [token, setToken] = useState(() => localStorage.getItem('memo_token'));

  useEffect(() => {
    const savedTheme = localStorage.getItem('memo_theme') || 'dark';
    document.body.classList.add(savedTheme + '-theme');
  }, []);
  
  useEffect(() => {
    const handleStorageChange = () => {
      const newToken = localStorage.getItem('memo_token');
      setToken(newToken);
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 主页面组件
  function HomePage() {
    const navigate = useNavigate();
    
    const [memos, setMemos] = useState([]);
    const [allMemos, setAllMemos] = useState([]); // 新增：存储所有备忘录（不分页）
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showEditor, setShowEditor] = useState(false);
    const [showHandwritingPanel, setShowHandwritingPanel] = useState(false);
    const [showCategoryManager, setShowCategoryManager] = useState(false);
    const [currentMemo, setCurrentMemo] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [user, setUser] = useState(() => {
      const savedUser = localStorage.getItem('memo_user');
      return savedUser ? JSON.parse(savedUser) : null;
    });

    const [currentTime, setCurrentTime] = useState(new Date());
    const [theme, setTheme] = useState(() => localStorage.getItem('memo_theme') || 'dark');
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const calculateUserStats = useCallback(() => {
        if (allMemos.length === 0) {
            return {
            total: 0,
            weeklyActiveDays: 0,
            monthlyActiveDays: 0,
            streakDays: 0,
            lastActive: null
            };
        }
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // 本周起始（周一）
        const dayOfWeek = today.getDay();
        const startOfWeek = new Date(today);
        if (dayOfWeek === 0) {
            startOfWeek.setDate(today.getDate() - 6);
        } else {
            startOfWeek.setDate(today.getDate() - (dayOfWeek - 1));
        }
        startOfWeek.setHours(0, 0, 0, 0);
        
        // 本月起始
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        
        // 收集日期
        const weekDays = new Set();
        const monthDays = new Set();
        let lastActiveDate = null;
        const memoDates = [];
        
        allMemos.forEach(memo => {
            const memoDate = new Date(memo.created_at);
            const dateStr = memoDate.toISOString().split('T')[0];
            memoDates.push(dateStr);
            
            // 更新最后活跃日期
            if (!lastActiveDate || memoDate > lastActiveDate) {
            lastActiveDate = memoDate;
            }
            
            // 本周活跃
            if (memoDate >= startOfWeek) {
            weekDays.add(dateStr);
            }
            
            // 本月活跃
            if (memoDate >= startOfMonth) {
            monthDays.add(dateStr);
            }
        });
        
        // 计算连续活跃天数
        let streakDays = 0;
        const sortedDates = [...new Set(memoDates)].sort((a, b) => b.localeCompare(a));
        
        let checkDate = today;
        while (true) {
            const checkDateStr = checkDate.toISOString().split('T')[0];
            if (sortedDates.includes(checkDateStr)) {
            streakDays++;
            checkDate.setDate(checkDate.getDate() - 1);
            checkDate.setHours(0, 0, 0, 0);
            } else {
            break;
            }
        }
        
        return {
            total: allMemos.length,
            weeklyActiveDays: weekDays.size,
            monthlyActiveDays: monthDays.size,
            streakDays,
            lastActive: lastActiveDate
        };
    }, [allMemos]);

    // 使用新的统计
    const userStats = calculateUserStats();

    const toggleTheme = () => {
      const newTheme = theme === 'dark' ? 'light' : 'dark';
      setTheme(newTheme);
      localStorage.setItem('memo_theme', newTheme);
      
      document.body.classList.remove(theme + '-theme');
      document.body.classList.add(newTheme + '-theme');
    };
    
    const currentCategoryRef = useRef(selectedCategory);


        // 计算本周活跃天数的新函数
    const calculateWeeklyActivity = useCallback(() => {
        if (allMemos.length === 0) return 0;
        
        // 获取本周的起始日期（周一）
        const today = new Date();
        const dayOfWeek = today.getDay(); // 0: 周日, 1: 周一, ..., 6: 周六
        const startOfWeek = new Date(today);
        
        // 如果是周日，则本周起始是上周日；否则是本周一
        if (dayOfWeek === 0) {
            startOfWeek.setDate(today.getDate() - 6); // 上周日
        } else {
            startOfWeek.setDate(today.getDate() - (dayOfWeek - 1)); // 本周一
        }
        
        startOfWeek.setHours(0, 0, 0, 0);
        
        // 收集本周内有备忘录的不同日期
        const uniqueDays = new Set();
        
        allMemos.forEach(memo => {
            const memoDate = new Date(memo.created_at);
            if (memoDate >= startOfWeek && memoDate <= today) {
            // 格式化为 YYYY-MM-DD 字符串来去重
            const dateStr = memoDate.toISOString().split('T')[0];
            uniqueDays.add(dateStr);
            }
        });
        
        return uniqueDays.size;
    }, [allMemos]);
    const weeklyActiveDays = calculateWeeklyActivity();

    // 计算统计数据（始终使用 allMemos）
    const calculateStats = useCallback(() => {
      const total = allMemos.length;
      const uncategorized = allMemos.filter(m => !m.category_id).length;
      const categorized = total - uncategorized;
      const handwritingCount = allMemos.filter(m => m.handwriting_mode).length;
      
      return { total, uncategorized, categorized, handwritingCount };
    }, [allMemos]);

    const stats = calculateStats();

    // 获取所有备忘录（用于统计）
    const fetchAllMemos = useCallback(async () => {
      try {
        const response = await apiClient.get('/memos');
        setAllMemos(response.data);
        return response.data;
      } catch (err) {
        console.error('获取所有备忘录失败:', err);
        return [];
      }
    }, []);

    // 获取当前筛选的备忘录
    const fetchMemos = useCallback(async () => {
      setLoading(true);
      setError('');
      try {
        let url = '/memos';
        const currentCat = currentCategoryRef.current;
        
        if (currentCat !== 'all') {
          url += `?category_id=${currentCat}`;
        }
        
        const response = await apiClient.get(url);
        setMemos(response.data);
      } catch (err) {
        console.error('获取备忘录失败:', err);
        setError('获取备忘录失败，请检查网络连接。');
        setMemos([]);
      } finally {
        setLoading(false);
      }
    }, []);

    const fetchCategories = useCallback(async () => {
      try {
        const response = await apiClient.get('/categories');
        setCategories(response.data);
        return response.data;
      } catch (err) {
        console.error('获取分类失败:', err);
        return [];
      }
    }, []);

    // 初始加载函数
    const initialLoad = useCallback(async () => {
      try {
        setLoading(true);
        // 并行加载所有必要数据
        const [memosData, categoriesData] = await Promise.all([
          fetchAllMemos(),
          fetchCategories()
        ]);
        
        // 获取当前分类的备忘录
        await fetchMemos();
        
        setInitialLoadComplete(true);
      } catch (err) {
        console.error('初始加载失败:', err);
        setError('加载失败，请刷新页面重试');
      } finally {
        setLoading(false);
      }
    }, [fetchAllMemos, fetchCategories, fetchMemos]);

    useEffect(() => {
      // 初始加载
      initialLoad();
      
      const timer = setInterval(() => {
        setCurrentTime(new Date());
      }, 1000);

      return () => {
        clearInterval(timer);
      };
    }, [initialLoad]);

    // 监听 selectedCategory 变化，获取筛选后的数据
    useEffect(() => {
      if (initialLoadComplete) {
        fetchMemos();
      }
    }, [selectedCategory, fetchMemos, initialLoadComplete]);

    const formatTime = (date) => {
      return date.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    };

    const formatDate = (date) => {
      const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const weekday = weekdays[date.getDay()];
      
      return `${year}年${month}月${day}日 ${weekday}`;
    };

    // 使用当前筛选的备忘录进行搜索
    const filteredMemos = memos.filter(memo => {
      const searchLower = searchQuery.toLowerCase();
      return (
        memo.title?.toLowerCase().includes(searchLower) ||
        memo.content?.toLowerCase().includes(searchLower) ||
        (memo.handwriting_mode && '手写'.toLowerCase().includes(searchLower))
      );
    });

    const handleSaveMemo = async (memoData) => {
      try {
        if (currentMemo) {
          await apiClient.put(`/memos/${currentMemo.id}`, memoData);
          console.log('备忘录更新成功');
        } else {
          await apiClient.post('/memos', memoData);
          console.log('备忘录创建成功');
        }
        setShowEditor(false);
        setCurrentMemo(null);
        // 刷新所有数据
        await fetchAllMemos();
        await fetchCategories();
        await fetchMemos();
      } catch (err) {
        console.error('保存备忘录失败:', err);
        alert(`保存失败: ${err.response?.data?.error || err.message}`);
      }
    };

    // 保存手写内容
    const handleSaveHandwriting = async (handwritingData) => {
      try {
        if (currentMemo) {
          await apiClient.put(`/memos/${currentMemo.id}`, handwritingData);
          console.log('手写备忘录更新成功');
        } else {
          await apiClient.post('/memos', handwritingData);
          console.log('手写备忘录创建成功');
        }
        
        setShowHandwritingPanel(false);
        setCurrentMemo(null);
        
        // 刷新数据
        await fetchAllMemos();
        await fetchMemos();
        
      } catch (err) {
        console.error('保存手写备忘录失败:', err);
        alert(`保存失败: ${err.response?.data?.error || err.message}`);
      }
    };

    const handleDeleteMemo = async (memoId) => {
      try {
        await apiClient.delete(`/memos/${memoId}`);
        console.log('备忘录删除成功');
        // 刷新所有数据
        await fetchAllMemos();
        await fetchCategories();
        await fetchMemos();
      } catch (err) {
        console.error('删除备忘录失败:', err);
        alert(`删除失败: ${err.response?.data?.error || err.message}`);
      }
    };

    const handleLogout = () => {
      localStorage.removeItem('memo_token');
      localStorage.removeItem('memo_user');
      navigate('/login');
    };

    const handleEditMemo = (memo) => {
      if (memo.handwriting_mode) {
        // 如果是手写备忘录，打开手写面板
        handleEditHandwriting(memo);
      } else {
        setCurrentMemo(memo);
        setShowEditor(true);
      }
    };

    const handleNewMemo = () => {
      setCurrentMemo(null);
      setShowEditor(true);
    };

    // 新建手写备忘录
    const handleNewHandwriting = () => {
      setCurrentMemo(null);
      setShowHandwritingPanel(true);
    };

    // 编辑手写备忘录
    const handleEditHandwriting = (memo) => {
      setCurrentMemo(memo);
      setShowHandwritingPanel(true);
    };

    const handleCancelEdit = () => {
      setShowEditor(false);
      setCurrentMemo(null);
    };

    // 修复：立即更新状态并获取数据
    const handleCategorySelect = useCallback(async (category) => {
      let newCategoryId;
      
      if (category === 'all') {
        newCategoryId = 'all';
      } else if (category === 'uncategorized') {
        newCategoryId = 'uncategorized';
      } else {
        newCategoryId = category.id;
      }
      
      // 如果当前已经是这个分类，不重复获取
      if (currentCategoryRef.current === newCategoryId) {
        return;
      }
      
      // 立即更新ref（同步）
      currentCategoryRef.current = newCategoryId;
      
      // 异步更新状态
      setSelectedCategory(newCategoryId);
      
      // 关闭分类管理面板（如果打开）
      setShowCategoryManager(false);
    }, []);

    const handleCategoryCreated = (newCategory) => {
      fetchCategories();
      // 自动选择新创建的分类
      handleCategorySelect(newCategory);
    };

    const handleCategoryUpdated = () => {
      fetchCategories();
      fetchAllMemos();
      fetchMemos();
    };

    const getSelectedCategoryInfo = () => {
      if (selectedCategory === 'all') {
        return { name: '全部备忘录', icon: '📁', color: '#58a6ff' };
      } else if (selectedCategory === 'uncategorized') {
        return { name: '未分类', icon: '📄', color: '#8b949e' };
      } else {
        const category = categories.find(cat => cat.id === selectedCategory);
        return category || { name: '未知分类', icon: '❓', color: '#8b949e' };
      }
    };

    const selectedCategoryInfo = getSelectedCategoryInfo();

    // 主页样式
    const styles = {
      pageContainer: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--color-bg-secondary)',
      },
      header: {
        backgroundColor: 'var(--color-header-bg)',
        color: 'var(--color-header-text)',
        borderBottom: '1px solid var(--color-header-border)',
        padding: '16px 0',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
      },
      headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '0 16px',
      },
      logoSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
      },
      logo: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: 'var(--color-header-text)',
        textDecoration: 'none',
        cursor: 'pointer',
      },
      logoText: {
        fontSize: '20px',
        fontWeight: '600',
        color: 'white',
      },
      navMenu: {
        display: 'flex',
        gap: '20px',
      },
      navLink: {
        color: 'var(--color-header-text)',
        textDecoration: 'none',
        fontSize: '14px',
        padding: '6px 12px',
        borderRadius: '4px',
        transition: 'background-color 0.2s',
      },
      userSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
      },
      newButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        fontSize: '14px',
      },
      plusIcon: {
        width: '16px',
        height: '16px',
      },
      logoutButton: {
        padding: '8px 16px',
        fontSize: '14px',
      },
      main: {
        flex: 1,
        padding: '32px 0',
      },
      container: {
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '0 16px',
      },
      statsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px',
      },
      statCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '20px',
      },
      contentArea: {
        backgroundColor: 'var(--color-bg-primary)',
        border: '1px solid var(--color-border-primary)',
        borderRadius: 'var(--border-radius)',
        padding: '24px',
      },
      contentHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '1px solid var(--color-border-primary)',
      },
      contentTitle: {
        fontSize: '24px',
        fontWeight: '600',
        color: 'var(--color-text-primary)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
      badge: {
        backgroundColor: 'var(--color-accent)',
        color: 'white',
        fontSize: '12px',
        padding: '2px 8px',
        borderRadius: '12px',
        fontWeight: '500',
      },
      loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 0',
      },
      largeSpinner: {
        width: '40px',
        height: '40px',
        border: '3px solid var(--color-border-primary)',
        borderTopColor: 'var(--color-accent)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '16px',
      },
      emptyState: {
        textAlign: 'center',
        padding: '60px 0',
      },
      emptyButton: {
        padding: '10px 20px',
        fontSize: '14px',
        marginTop: '16px',
      },
      memoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '16px',
      },
      categoryPills: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: '8px',
      },
      categoryPill: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: '1px solid transparent',
      },
      categoryPillSelected: {
        borderColor: 'var(--color-accent)',
        backgroundColor: 'rgba(88, 166, 255, 0.1)',
        fontWeight: '500',
      },
      memoCategoryBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '3px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        marginLeft: '8px',
      },
      handwritingBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '3px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        marginLeft: '8px',
        backgroundColor: '#6f42c120',
        color: '#6f42c1',
      }
    };

    useEffect(() => {
      const styleSheet = document.createElement('style');
      styleSheet.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes subtlePulse {
          0% { opacity: 1; }
          50% { opacity: 0.95; }
          100% { opacity: 1; }
        }
        
        .time-container {
          animation: subtlePulse 3s infinite ease-in-out;
        }
        
        .nav-link:hover {
          background-color: rgba(255,255,255,0.1);
        }
        
        .memo-card:hover {
          border-color: var(--color-accent);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        
        @media (max-width: 768px) {
          .header-container {
            flex-direction: column;
            gap: 16px;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .stats-card {
                min-width: auto !important;
           }
          .logo-section {
            width: 100%;
            justify-content: space-between;
          }
          
          .user-section {
            width: 100%;
            justify-content: space-between;
          }
          
          .memo-grid {
            grid-template-columns: 1fr;
          }
          
          .time-display-wrapper {
            order: 3;
            width: 100%;
            margin-top: 15px;
          }
          
          .time-display {
            font-size: 20px;
          }
          
          .category-pills {
            overflow-x: auto;
            flex-wrap: nowrap;
            padding-bottom: 10px;
          }
        }
      `;
      document.head.appendChild(styleSheet);
      return () => {
        document.head.removeChild(styleSheet);
      };
    }, []);

    return (
      <div style={styles.pageContainer}>
        <header style={styles.header}>
          <div style={styles.headerContainer}>
            <div style={styles.logoSection}>
              <div style={styles.logo} onClick={() => window.location.href = '/'}>
                <MemoLogo size={32} />
                <span style={styles.logoText}>MemoHub</span>
              </div>
              
              <nav style={styles.navMenu}>
                <a href="/" style={styles.navLink} className="nav-link">首页</a>
                <a href="/" style={styles.navLink} className="nav-link" onClick={(e) => { e.preventDefault(); handleNewMemo(); }}>新建</a>
                <a href="#" style={styles.navLink} className="nav-link" onClick={(e) => { e.preventDefault(); handleNewHandwriting(); }}>
                  手写
                </a>
                <a href="/game-center" className="nav-link game-center-nav">🎮 游戏中心</a>
                <a href="#" style={styles.navLink} className="nav-link" onClick={(e) => { e.preventDefault(); setShowCategoryManager(!showCategoryManager); }}>
                  {showCategoryManager ? '返回列表' : '分类'}
                </a>
              </nav>
            </div>
            
            <div style={styles.userSection}>
              <button 
                  onClick={toggleTheme}
                  className="theme-toggle"
                  style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-header-text)'
                  }}
                  aria-label="切换主题"
              >
                  {theme === 'dark' ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#f5f0e6">
                      <path d="M9.37 5.51c-.18.64-.27 1.31-.27 1.99 0 4.08 3.32 7.4 7.4 7.4 .68 0 1.35-.09 1.99-.27C17.45 17.19 14.93 19 12 19c-3.86 0-7-3.14-7-7 0-2.93 1.81-5.45 4.37-6.49zM12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z"/>
                  </svg>
                  ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffd8b1">
                      <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41.39.39 1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41.39.39 1.03.39 1.41 0l1.06-1.06z"/>
                  </svg>
                  )}
              </button>

              <button 
                onClick={handleNewMemo} 
                className="btn btn-primary"
                style={styles.newButton}
              >
                <svg style={styles.plusIcon} viewBox="0 0 16 16" fill="currentColor">
                  <path fillRule="evenodd" d="M7.75 2a.75.75 0 01.75.75V7h4.25a.75.75 0 110 1.5H8.5v4.25a.75.75 0 11-1.5 0V8.5H2.75a.75.75 0 010-1.5H7V2.75A.75.75 0 017.75 2z"/>
                </svg>
                新建备忘录
              </button>
              
              <button 
                onClick={handleLogout} 
                className="btn btn-danger"
                style={styles.logoutButton}
              >
                退出登录
              </button>
            </div>
          </div>
        </header>

        <main style={styles.main}>
          <div style={styles.container}>
            {/* 用户状态与提示面板（包含实时时间） */}
            {user && (
              <div className="card" style={{ 
                marginBottom: '25px', 
                padding: '20px', 
                backgroundColor: theme === 'dark' ? 'rgba(3, 102, 214, 0.1)' : 'rgba(3, 102, 214, 0.05)' 
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  flexWrap: 'wrap',
                  gap: '20px'
                }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ 
                      marginTop: 0, 
                      display: 'flex', 
                      alignItems: 'center',
                      color: 'var(--color-text-primary)'
                    }}>
                      <span style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '50%', 
                        backgroundColor: 'var(--color-accent)', 
                        color: 'white', 
                        marginRight: '10px',
                        fontSize: '14px'
                      }}>
                        {user.username?.charAt(0).toUpperCase() || 'U'}
                      </span>
                      你好，{user.username}！
                    </h4>
                    <p style={{ 
                      color: 'var(--color-text-secondary)', 
                      marginBottom: 0, 
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}>
                      你已累计记录 <strong style={{color: 'var(--color-accent)'}}>{stats.total}</strong> 条备忘录，
                      本周活跃 <strong style={{color: '#28a745'}}>{weeklyActiveDays}</strong> 天。
                      连续活跃 <strong style={{color: '#d73a49'}}>{userStats.streakDays}</strong> 天。
                      {stats.total === 0 && ' 写下第一条想法吧！'}
                    </p>
                    {userStats.lastActive && (
                        <p style={{ 
                        fontSize: '12px', 
                        color: 'var(--color-text-tertiary)', 
                        marginTop: '4px'
                        }}>
                        最后活跃：{userStats.lastActive.toLocaleDateString('zh-CN')}
                        </p>
                    )}
                  </div>
                  
                  {/* 实时时间显示 - 根据主题动态调整样式 */}
                  <div className="time-container" style={{ 
                    textAlign: 'center', 
                    padding: '12px 18px',
                    backgroundColor: theme === 'dark' ? 'rgba(22, 27, 34, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '10px',
                    minWidth: '200px',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
                    border: `1px solid ${theme === 'dark' ? 'rgba(48, 54, 61, 0.6)' : 'var(--color-border-primary)'}`,
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: theme === 'dark' ? '#58a6ff' : '#0969da',
                      fontFamily: 'monospace, sans-serif',
                      textAlign: 'center',
                      textShadow: theme === 'dark' ? '0 0 8px rgba(88, 166, 255, 0.3)' : 'none'
                    }}>
                      {formatTime(currentTime)}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: theme === 'dark' ? '#c9d1d9' : '#24292f',
                      marginTop: '4px',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {formatDate(currentTime)}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: theme === 'dark' ? '#8b949e' : '#57606a',
                      marginTop: '8px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '4px',
                      opacity: 0.8
                    }}>
                      <span style={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        backgroundColor: theme === 'dark' ? '#238636' : '#2da44e',
                        display: 'inline-block',
                        boxShadow: theme === 'dark' ? '0 0 4px rgba(35, 134, 54, 0.5)' : 'none'
                      }}></span>
                      在线 · 时间实时更新
                    </div>
                  </div>
                  
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => alert(`用户设置页面（可修改主题、通知等）`)}
                    style={{ fontSize: '14px', whiteSpace: 'nowrap' }}
                  >
                    ⚙️ 个性化设置
                  </button>
                </div>
              </div>
            )}

            {/* 个人统计看板 - 始终显示全局统计数据 */}
            <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px', color: 'var(--color-text-primary)' }}>📊 全局数据统计</h3>
            
            {/* 第一行：基础统计数据 */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                <div style={{ textAlign: 'center', padding: '15px', flex: 1, minWidth: '120px', backgroundColor: 'var(--color-bg-secondary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--color-accent)' }}>{stats.total}</div>
                  <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>备忘录总数</div>
                </div>
                <div style={{ textAlign: 'center', padding: '15px', flex: 1, minWidth: '120px', backgroundColor: 'var(--color-bg-secondary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#28a745' }}>{stats.categorized}</div>
                  <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>已分类</div>
                </div>
                <div style={{ textAlign: 'center', padding: '15px', flex: 1, minWidth: '120px', backgroundColor: 'var(--color-bg-secondary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#fd7e14' }}>{stats.uncategorized}</div>
                  <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>未分类</div>
                </div>
                <div style={{ textAlign: 'center', padding: '15px', flex: 1, minWidth: '120px', backgroundColor: 'var(--color-bg-secondary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#6f42c1' }}>{stats.handwritingCount}</div>
                  <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>手写备忘录</div>
                </div>
            </div>
            
            {/* 第二行：活跃度统计数据 */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                <div style={{ textAlign: 'center', padding: '15px', flex: 1, minWidth: '120px', backgroundColor: 'var(--color-bg-secondary)', borderRadius: '8px' }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#28a745' }}>{userStats.weeklyActiveDays}</div>
                <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>本周活跃</div>
                </div>
                <div style={{ textAlign: 'center', padding: '15px', flex: 1, minWidth: '120px', backgroundColor: 'var(--color-bg-secondary)', borderRadius: '8px' }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#d73a49' }}>{userStats.streakDays}</div>
                <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>连续活跃</div>
                </div>
                <div style={{ textAlign: 'center', padding: '15px', flex: 1, minWidth: '120px', backgroundColor: 'var(--color-bg-secondary)', borderRadius: '8px' }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#6f42c1' }}>{userStats.monthlyActiveDays}</div>
                <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>本月活跃</div>
                </div>
                <div style={{ textAlign: 'center', padding: '15px', flex: 1, minWidth: '120px', backgroundColor: 'var(--color-bg-secondary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#0366d6' }}>
                    {userStats.lastActive ? 
                      Math.floor((new Date() - userStats.lastActive) / (1000 * 60 * 60 * 24))
                      : '-'
                    }
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>距上次活跃</div>
                </div>
              </div>
            </div>

            {/* 分类筛选器 */}
            <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
              <h4 style={{ marginTop: 0, marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-text-primary)' }}>
                <span style={{ fontSize: '20px' }}>{selectedCategoryInfo.icon}</span>
                {selectedCategoryInfo.name}
                <span style={{ 
                  fontSize: '12px', 
                  backgroundColor: 'var(--color-bg-secondary)', 
                  color: 'var(--color-text-secondary)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  marginLeft: '8px'
                }}>
                  {filteredMemos.length} 条
                </span>
              </h4>
              
              <div className="category-pills" style={styles.categoryPills}>
                <div
                  key={`category-all-${selectedCategory}`}
                  style={{
                    ...styles.categoryPill,
                    ...(selectedCategory === 'all' ? styles.categoryPillSelected : {}),
                    backgroundColor: selectedCategory === 'all' ? 'rgba(88, 166, 255, 0.1)' : 'var(--color-bg-primary)',
                    borderColor: selectedCategory === 'all' ? '#58a6ff' : 'var(--color-border-primary)'
                  }}
                  onClick={() => handleCategorySelect('all')}
                >
                  <span>📁</span>
                  <span>全部</span>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                    ({stats.total})
                  </span>
                </div>
                
                <div
                  key={`category-uncat-${selectedCategory}`}
                  style={{
                    ...styles.categoryPill,
                    ...(selectedCategory === 'uncategorized' ? styles.categoryPillSelected : {}),
                    backgroundColor: selectedCategory === 'uncategorized' ? 'rgba(139, 148, 158, 0.1)' : 'var(--color-bg-primary)',
                    borderColor: selectedCategory === 'uncategorized' ? '#8b949e' : 'var(--color-border-primary)'
                  }}
                  onClick={() => handleCategorySelect('uncategorized')}
                >
                  <span>📄</span>
                  <span>未分类</span>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                    ({stats.uncategorized})
                  </span>
                </div>
                
                {categories.map(category => (
                  <div
                    key={`category-${category.id}-${selectedCategory}`}
                    style={{
                      ...styles.categoryPill,
                      ...(selectedCategory === category.id ? styles.categoryPillSelected : {}),
                      backgroundColor: selectedCategory === category.id 
                        ? `rgba(${parseInt(category.color.slice(1, 3), 16)}, ${parseInt(category.color.slice(3, 5), 16)}, ${parseInt(category.color.slice(5, 7), 16)}, 0.1)`
                        : 'var(--color-bg-primary)',
                      borderColor: selectedCategory === category.id ? category.color : 'var(--color-border-primary)',
                      color: selectedCategory === category.id ? category.color : 'var(--color-text-primary)'
                    }}
                    onClick={() => handleCategorySelect(category)}
                  >
                    <span>{category.icon}</span>
                    <span>{category.name}</span>
                    <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                      ({category.memo_count || 0})
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* 搜索与过滤栏 */}
            <div className="card" style={{ marginBottom: '20px', padding: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center' }}>
              <div style={{ flexGrow: 1, minWidth: '250px' }}>
                <input
                  type="text"
                  placeholder="🔍 搜索备忘录标题、内容或类型..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-field"
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button className="btn btn-secondary" onClick={() => alert('按日期筛选功能开发中...')}>📅 按日期筛选</button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setSearchQuery('');
                    handleCategorySelect('all');
                  }}
                >
                  🔄 重置筛选
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={handleNewHandwriting}
                >
                  ✍️ 新建手写
                </button>
              </div>
            </div>

            {/* 分类管理面板 */}
            {showCategoryManager && (
              <CategoryManager
                onCategorySelect={handleCategorySelect}
                onCategoryCreated={handleCategoryCreated}
                onCategoryUpdated={handleCategoryUpdated}
              />
            )}

            {/* 主要内容区域 */}
            {!showCategoryManager && (
              <div style={styles.contentArea}>
                <div style={styles.contentHeader}>
                  <h2 style={styles.contentTitle}>
                    {selectedCategoryInfo.icon} {selectedCategoryInfo.name}
                    <span style={styles.badge}>{filteredMemos.length}</span>
                  </h2>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="在当前分类内搜索..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="input-field"
                      style={{ width: '200px' }}
                    />
                    {searchQuery && (
                      <button 
                        onClick={() => setSearchQuery('')}
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px', fontSize: '14px' }}
                      >
                        清除
                      </button>
                    )}
                  </div>
                </div>
                
                {/* 加载状态 */}
                {loading && (
                  <div style={styles.loadingContainer}>
                    <div style={styles.largeSpinner}></div>
                    <p style={{ color: 'var(--color-text-secondary)' }}>加载备忘录中...</p>
                  </div>
                )}
                
                {/* 错误提示 */}
                {error && (
                  <div style={{ 
                    padding: '20px', 
                    backgroundColor: 'rgba(203, 36, 49, 0.05)', 
                    border: '1px solid rgba(203, 36, 49, 0.2)',
                    borderRadius: 'var(--border-radius)',
                    marginBottom: '20px'
                  }}>
                    <p style={{ color: 'var(--color-btn-danger)', margin: 0 }}>{error}</p>
                    <button 
                      onClick={fetchMemos}
                      className="btn btn-primary"
                      style={{ marginTop: '10px', padding: '6px 12px', fontSize: '14px' }}
                    >
                      重试
                    </button>
                  </div>
                )}
                
                {/* 空状态 */}
                {!loading && !error && filteredMemos.length === 0 && (
                  <div style={styles.emptyState}>
                    <svg 
                      style={{ 
                        width: '64px', 
                        height: '64px', 
                        color: 'var(--color-text-tertiary)',
                        marginBottom: '16px'
                      }} 
                      viewBox="0 0 16 16" 
                      fill="currentColor"
                    >
                      <path fillRule="evenodd" d="M1.75 1.5a.25.25 0 00-.25.25v12.5c0 .138.112.25.25.25h12.5a.25.25 0 00.25-.25V1.75a.25.25 0 00-.25-.25H1.75zM0 1.75C0 .784.784 0 1.75 0h12.5C15.216 0 16 .784 16 1.75v12.5A1.75 1.75 0 0114.25 16H1.75A1.75 1.75 0 010 14.25V1.75zm8 1.5a.75.75 0 01.75.75v4a.75.75 0 01-1.5 0V4A.75.75 0 018 3.25zm0 7a1 1 0 100-2 1 1 0 000 2z"/>
                    </svg>
                    <h3 style={{ 
                      fontSize: '20px', 
                      fontWeight: '600', 
                      color: 'var(--color-text-primary)',
                      marginBottom: '8px'
                    }}>
                      {searchQuery ? '没有找到相关备忘录' : `当前分类还没有备忘录`}
                    </h3>
                    <p style={{ 
                      fontSize: '14px', 
                      color: 'var(--color-text-secondary)',
                      marginBottom: '20px'
                    }}>
                      {searchQuery ? 
                        '尝试使用不同的关键词搜索' : 
                        selectedCategory === 'uncategorized' ?
                        '尝试为一些备忘录添加分类' :
                        selectedCategory === 'all' ?
                        '点击"新建备忘录"或"手写"按钮开始记录吧！' :
                        `点击"新建备忘录"按钮为${selectedCategoryInfo.name}添加内容`
                      }
                    </p>
                    {!searchQuery && (
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        <button 
                          onClick={handleNewMemo}
                          className="btn btn-primary"
                          style={styles.emptyButton}
                        >
                          {selectedCategory === 'uncategorized' ? '查看全部备忘录' : '新建文本备忘录'}
                        </button>
                        <button 
                          onClick={handleNewHandwriting}
                          className="btn btn-success"
                          style={styles.emptyButton}
                        >
                          ✍️ 新建手写备忘录
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* 备忘录列表 */}
                {!loading && !error && filteredMemos.length > 0 && (
                  <div style={styles.memoGrid}>
                    {filteredMemos.map((memo) => (
                      memo.handwriting_mode ? (
                        // 手写备忘录使用专门的预览组件
                        <HandwritingPreview
                          key={memo.id}
                          memo={memo}  // 传递完整的 memo 对象
                          imageData={memo.handwriting_data}
                          title={memo.title}
                          onEdit={() => handleEditHandwriting(memo)}
                          onDelete={() => {
                            if (window.confirm(`确定要删除"${memo.title}"吗？`)) {
                              handleDeleteMemo(memo.id);
                            }
                          }}
                          theme={theme}
                        />
                      ) : (
                        // 普通备忘录使用原有卡片
                        <div key={memo.id} className="card memo-card" style={{ 
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          borderLeft: memo.category?.color ? `4px solid ${memo.category.color}` : '4px solid var(--color-border-primary)'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            marginBottom: '12px'
                          }}>
                            <div style={{ flex: 1 }}>
                              <h3 
                                style={{ 
                                  fontSize: '16px', 
                                  fontWeight: '600', 
                                  color: 'var(--color-accent)',
                                  margin: 0,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onClick={() => handleEditMemo(memo)}
                              >
                                {memo.title || '无标题'}
                                {memo.category && (
                                  <span style={{
                                    ...styles.memoCategoryBadge,
                                    backgroundColor: `${memo.category.color}20`,
                                    color: memo.category.color
                                  }}>
                                    <span>{memo.category.icon}</span>
                                    <span>{memo.category.name}</span>
                                  </span>
                                )}
                              </h3>
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button 
                                onClick={() => handleEditMemo(memo)}
                                className="btn btn-secondary"
                                style={{ padding: '3px 8px', fontSize: '12px' }}
                              >
                                编辑
                              </button>
                              <button 
                                onClick={() => {
                                  if (window.confirm(`确定要删除"${memo.title}"吗？`)) {
                                    handleDeleteMemo(memo.id);
                                  }
                                }}
                                className="btn btn-danger"
                                style={{ padding: '3px 8px', fontSize: '12px' }}
                              >
                                删除
                              </button>
                            </div>
                          </div>
                          
                          <div style={{ marginBottom: '12px' }}>
                            {memo.content ? (
                              <p style={{ 
                                fontSize: '14px', 
                                color: 'var(--color-text-secondary)', 
                                lineHeight: '1.5',
                                margin: 0,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}>
                                {memo.content.length > 150 
                                  ? memo.content.substring(0, 150) + '...' 
                                  : memo.content}
                              </p>
                            ) : (
                              <p style={{ 
                                fontSize: '14px', 
                                color: 'var(--color-text-tertiary)', 
                                fontStyle: 'italic',
                                margin: 0
                              }}>
                                无内容
                              </p>
                            )}
                          </div>
                          
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            paddingTop: '12px',
                            borderTop: '1px solid var(--color-border-primary)'
                          }}>
                            <div style={{ 
                              fontSize: '12px', 
                              color: 'var(--color-text-tertiary)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <svg style={{ width: '12px', height: '12px' }} viewBox="0 0 16 16" fill="currentColor">
                                <path fillRule="evenodd" d="M1.75 8a6.25 6.25 0 1112.5 0 6.25 6.25 0 01-12.5 0zM8 0a8 8 0 100 16A8 8 0 008 0zm.75 4.25a.75.75 0 00-1.5 0v4c0 .414.336.75.75.75h2.5a.75.75 0 000-1.5H8.75V4.25z"/>
                              </svg>
                              {new Date(memo.created_at).toLocaleString('zh-CN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                            
                            <div>
                              <span style={{ 
                                fontSize: '12px', 
                                color: 'var(--color-text-tertiary)',
                                backgroundColor: 'var(--color-bg-secondary)',
                                padding: '2px 6px',
                                borderRadius: 'var(--border-radius-small)'
                              }}>
                                {memo.content?.length || 0} 字
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>

        {/* 备忘录编辑器弹窗 */}
        {showEditor && (
          <MemoEditor
            memoToEdit={currentMemo}
            onSave={handleSaveMemo}
            onCancel={handleCancelEdit}
          />
        )}
        
        {/* 手写画板弹窗 */}
        {showHandwritingPanel && (
          <HandwritingPanel
            memoId={currentMemo?.id}
            onSave={handleSaveHandwriting}
            onCancel={() => {
              setShowHandwritingPanel(false);
              setCurrentMemo(null);
            }}
            theme={theme}
          />
        )}
        
        {/* 页脚 */}
        <footer style={{
          backgroundColor: 'var(--color-header-bg)',
          color: 'var(--color-header-text)',
          borderTop: '1px solid var(--color-header-border)',
          padding: '24px 0',
          marginTop: 'auto'
        }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 16px' }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MemoLogo size={24} theme="dark" />
                <span style={{ fontSize: '14px', color: 'var(--color-header-text)', opacity: 0.8 }}>
                  MemoHub - 智能分类备忘录（支持手写功能）
                </span>
              </div>
              <div style={{ display: 'flex', gap: '24px' }}>
                <a href="#" style={{ color: 'var(--color-header-text)', textDecoration: 'none', fontSize: '12px', opacity: 0.8 }}>
                  帮助
                </a>
                <a href="/privacy" style={{ color: 'var(--color-header-text)', textDecoration: 'none', fontSize: '12px', opacity: 0.8 }}>
                  隐私
                </a>
                <a href="/terms" style={{ color: 'var(--color-header-text)', textDecoration: 'none', fontSize: '12px', opacity: 0.8 }}>
                  条款
                </a>
                <a href="#" style={{ color: 'var(--color-header-text)', textDecoration: 'none', fontSize: '12px', opacity: 0.8 }}>
                  关于
                </a>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-header-text)', opacity: 0.6 }}>
                © {new Date().getFullYear()} MemoHub. 手写功能已上线！
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          <Route path="/" element={token ? <HomePage /> : <Navigate to="/login" />} />
          <Route path="/login" element={<Login setToken={setToken} />} />
          <Route path="/game-center" element={token ? <GameCenter /> : <Navigate to="/login" />} />
          <Route path="/conquest" element={token ? <ConquestLobby token={token} /> : <Navigate to="/login" />} />
          <Route path="/register" element={<Register />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/game" element={token ? <GamePage /> : <Navigate to="/login" />} />
          <Route path="/minesweeper" element={token ? <MinesweeperLobby token={token} /> : <Navigate to="/login" />} />
          <Route path="/flappy" element={token ? <FlappyBirdLobby token={token} /> : <Navigate to="/login" />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;